import React, { useState } from 'react';
import { SearchIcon, InfoIcon, SaveIcon } from 'lucide-react';
export const CareerGenomeMapper: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDimension, setExpandedDimension] = useState<string | null>(null);
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  // Sample data for Career Genome dimensions
  const dimensions = [{
    id: 'creativity',
    name: 'Creativity',
    description: 'Ability to generate novel ideas and solutions',
    assessments: [{
      name: 'MBTI',
      weight: 30
    }, {
      name: 'Astrological Chart',
      weight: 20
    }, {
      name: 'DMIT',
      weight: 50
    }]
  }, {
    id: 'analytical',
    name: 'Analytical Thinking',
    description: 'Ability to break down complex problems and analyze data',
    assessments: [{
      name: 'MBTI',
      weight: 40
    }, {
      name: 'DMIT',
      weight: 40
    }, {
      name: 'Numerology',
      weight: 20
    }]
  }, {
    id: 'communication',
    name: 'Communication',
    description: 'Ability to clearly convey information and ideas to others',
    assessments: [{
      name: 'MBTI',
      weight: 50
    }, {
      name: 'Career Interest Inventory',
      weight: 30
    }, {
      name: 'Astrological Chart',
      weight: 20
    }]
  }, {
    id: 'leadership',
    name: 'Leadership',
    description: 'Ability to guide and motivate others towards a common goal',
    assessments: [{
      name: 'MBTI',
      weight: 35
    }, {
      name: 'Career Interest Inventory',
      weight: 35
    }, {
      name: 'Human Design',
      weight: 30
    }]
  }, {
    id: 'technical',
    name: 'Technical Aptitude',
    description: 'Proficiency with tools, systems, and technical concepts',
    assessments: [{
      name: 'DMIT',
      weight: 60
    }, {
      name: 'Career Interest Inventory',
      weight: 40
    }]
  }, {
    id: 'adaptability',
    name: 'Adaptability',
    description: 'Ability to adjust to new conditions and environments',
    assessments: [{
      name: 'Human Design',
      weight: 40
    }, {
      name: 'MBTI',
      weight: 30
    }, {
      name: 'Geographic Context',
      weight: 30
    }]
  }, {
    id: 'empathy',
    name: 'Empathy',
    description: 'Ability to understand and share the feelings of others',
    assessments: [{
      name: 'MBTI',
      weight: 40
    }, {
      name: 'Astrological Chart',
      weight: 30
    }, {
      name: 'Family Background',
      weight: 30
    }]
  }];
  const filteredDimensions = dimensions.filter(dimension => dimension.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const toggleDimension = (id: string) => {
    if (expandedDimension === id) {
      setExpandedDimension(null);
    } else {
      setExpandedDimension(id);
    }
  };
  const handleSave = () => {
    // In a real app, this would save changes to the database
    setShowSaveMessage(true);
    setTimeout(() => setShowSaveMessage(false), 3000);
  };
  return <div data-id="element-104">
      <div className="mb-6" data-id="element-105">
        <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-106">
          Career Genome Mapper
        </h1>
        <p className="text-gray-600" data-id="element-107">
          Configure how assessments contribute to career genome dimensions
        </p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-108">
        <div className="p-6 border-b border-gray-200" data-id="element-109">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0" data-id="element-110">
            <div className="relative" data-id="element-111">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-112">
                <SearchIcon size={16} className="text-gray-400" data-id="element-113" />
              </div>
              <input type="text" placeholder="Search dimensions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4] w-full sm:w-64" data-id="element-114" />
            </div>
            <div className="flex items-center text-sm text-gray-600" data-id="element-115">
              <InfoIcon size={16} className="mr-2 text-gray-400" data-id="element-116" />
              <span data-id="element-117">Changes are automatically saved to the system</span>
            </div>
          </div>
        </div>
        <div className="overflow-hidden" data-id="element-118">
          <div className="space-y-4 p-6" data-id="element-119">
            {filteredDimensions.map(dimension => <div key={dimension.id} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-120">
                <div className="p-4 bg-gray-50 flex justify-between items-center cursor-pointer" onClick={() => toggleDimension(dimension.id)} data-id="element-121">
                  <div data-id="element-122">
                    <h3 className="text-lg font-medium text-gray-900" data-id="element-123">
                      {dimension.name}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-124">
                      {dimension.description}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-125">
                    {expandedDimension === dimension.id ? '▼' : '▶'}
                  </div>
                </div>
                {expandedDimension === dimension.id && <div className="p-4 border-t border-gray-200" data-id="element-126">
                    <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-127">
                      Assessment Contributions
                    </h4>
                    <div className="space-y-4" data-id="element-128">
                      {dimension.assessments.map((assessment, index) => <div key={index} data-id="element-129">
                          <div className="flex justify-between items-center mb-1" data-id="element-130">
                            <label className="text-sm text-gray-700" data-id="element-131">
                              {assessment.name}
                            </label>
                            <span className="text-sm text-gray-500" data-id="element-132">
                              {assessment.weight}%
                            </span>
                          </div>
                          <div className="flex items-center" data-id="element-133">
                            <input type="range" min="0" max="100" value={assessment.weight} className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer" data-id="element-134" />
                          </div>
                        </div>)}
                      <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-135">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-136">
                          Link Additional Assessments
                        </h4>
                        <div className="flex" data-id="element-137">
                          <select className="border border-gray-300 rounded-md py-1 px-2 mr-2 flex-1 focus:outline-none focus:ring-2 focus:ring-[#7D56F4] focus:border-[#7D56F4]" data-id="element-138">
                            <option value="" data-id="element-139">Select an assessment</option>
                            <option value="iridology" data-id="element-140">Iridology</option>
                            <option value="big-five" data-id="element-141">
                              Big Five Personality
                            </option>
                            <option value="holland" data-id="element-142">Holland Code</option>
                          </select>
                          <button className="bg-[#7D56F4] text-white py-1 px-4 rounded-md hover:bg-[#6A46E5]" data-id="element-143">
                            Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>)}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center" data-id="element-144">
          <div className="text-sm text-gray-700" data-id="element-145">
            Showing{' '}
            <span className="font-medium" data-id="element-146">{filteredDimensions.length}</span> of{' '}
            <span className="font-medium" data-id="element-147">{dimensions.length}</span> dimensions
          </div>
          <button onClick={handleSave} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#7D56F4] hover:bg-[#6A46E5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7D56F4]" data-id="element-148">
            <SaveIcon size={16} className="mr-2" data-id="element-149" />
            Save All Changes
          </button>
        </div>
      </div>
      {/* Save confirmation message */}
      {showSaveMessage && <div className="fixed bottom-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md" data-id="element-150">
          <div className="flex" data-id="element-151">
            <div className="flex-shrink-0" data-id="element-152">
              <svg className="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-153">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-154" />
              </svg>
            </div>
            <div className="ml-3" data-id="element-155">
              <p className="text-sm" data-id="element-156">Changes saved successfully!</p>
            </div>
          </div>
        </div>}
    </div>;
};